/**
 * Viru Trend Dynamics — Dashboard Overlay
 *
 * Top-right TradingView-style info table replicating the VTD dashboard:
 *   - header: script name + "VTD" tag, with the live status right-aligned
 *   - Target Mode / Entry / Protective SL / Target
 *   - Trailing Method / Trail Activation / Re-entry / Exit Symbols legend
 *   - risk (1R), reward, open P/L in R, closed P/L and a win/loss tally
 *
 * Responsive behaviour
 *   `priceScaleWidth` keeps the table clear of the chart's price axis; it is
 *   published as the `--vtd-right` custom property so the stylesheet can also
 *   derive a fitting `max-width` (the chart panel clips at its own edges, so an
 *   oversized table would otherwise be cut off on the left).
 *
 *   The type scale and label column step down at each breakpoint, and on phones
 *   the expanded table is capped at 58vh with a scrolling body so it never
 *   swallows the chart. Collapsing shows a one-line summary of the live trade.
 *
 * CSS prefix: vtd-
 */
import React from "react";
import { VtdDashboard } from "../../indicators/built-in/viru-trend-dynamics";
import "./VtdOverlay.scss";

interface VtdOverlayProps {
  dashboard: VtdDashboard | null;
  priceScaleWidth: number;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
}

const num = (v: number | null, digits = 2) =>
  v === null || !isFinite(v) ? "—" : v.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/** One label / value pair. */
const Row: React.FC<{
  label: string;
  children: React.ReactNode;
  valueClass?: string;
  labelClass?: string;
}> = ({ label, children, valueClass, labelClass }) => (
  <div className="vtd-row">
    <span className={`vtd-label${labelClass ? ` ${labelClass}` : ""}`}>{label}</span>
    <span className={`vtd-value${valueClass ? ` ${valueClass}` : ""}`}>{children}</span>
  </div>
);

const ViruTrendDynamicsOverlay: React.FC<VtdOverlayProps> = ({
  dashboard, priceScaleWidth, visible, showDashboard, onToggleDashboard,
}) => {
  if (!visible) return null;

  const d = dashboard;
  const right = (priceScaleWidth > 0 ? priceScaleWidth : 62) + 14;

  const statusClass =
    !d ? "vtd-status-wait" :
    d.direction === "BUY" ? "vtd-status-buy" :
    d.direction === "SELL" ? "vtd-status-sell" :
    d.status === "BULLISH BIAS" ? "vtd-status-buy" :
    d.status === "BEARISH BIAS" ? "vtd-status-sell" : "vtd-status-wait";

  // Collapsed one-liner so a collapsed table still says something useful.
  const collapsedText = !d
    ? "—"
    : d.direction
      ? `${d.status} · ${num(d.entry)} / ${num(d.protectiveSL)}${d.target !== null ? ` / ${num(d.target)}` : ""}`
      : d.status;

  return (
    <div
      className={`vtd-dash${showDashboard ? " vtd-dash--open" : ""}`}
      style={{ ["--vtd-right" as any]: `${right}px` }}
    >
      <div className="vtd-head">
        <span className="vtd-title">Viru Trend Dynamics</span>
        <span className="vtd-tag">VTD</span>
        <button
          className="vtd-toggle"
          onClick={onToggleDashboard}
          title={showDashboard ? "Collapse dashboard" : "Expand dashboard"}
          aria-label={showDashboard ? "Collapse dashboard" : "Expand dashboard"}
          aria-expanded={showDashboard}
        >
          <span className="vtd-toggle-icon" aria-hidden="true">{showDashboard ? "▲" : "▼"}</span>
        </button>
      </div>

      {showDashboard && d ? (
        <>
          <div className="vtd-row vtd-row--status">
            <span className="vtd-label vtd-label--plain" />
            <span className={`vtd-value vtd-value--status ${statusClass}`}>{d.status}</span>
          </div>

          <Row label="Target Mode">{d.targetMode}</Row>
          <Row label="Entry" valueClass="vtd-value--entry">{num(d.entry)}</Row>
          <Row label="Protective SL" valueClass="vtd-value--sl">{num(d.protectiveSL)}</Row>
          <Row label="Target" valueClass="vtd-value--tp">{num(d.target)}</Row>
          <Row
            label="Open P/L"
            valueClass={d.openPL >= 0 ? "vtd-value--buy" : "vtd-value--sell"}
          >
            {d.openPL.toFixed(2)} ({d.openR.toFixed(2)}R)
          </Row>

          <Row label="Risk (1R)">{num(d.risk)}</Row>
          <Row label="Reward">{num(d.reward)}</Row>
          <Row label="Trailing Method">{d.trailingMethod}</Row>
          <Row label="Trail Activation">{d.trailActivation}</Row>
          <Row label="Re-entry" valueClass="vtd-value--muted">{d.reentry}</Row>
          <Row
            label="Closed P/L"
            valueClass={d.closedPL >= 0 ? "vtd-value--buy" : "vtd-value--sell"}
          >
            {d.closedPL.toFixed(2)}
          </Row>
          <Row label="Trades" valueClass="vtd-value--muted">
            {d.trades} · {d.wins}W / {d.losses}L
          </Row>
          <Row label="Exit Symbols" valueClass="vtd-value--legend">
            <i className="vtd-dot vtd-dot--tp" />TP{" "}
            <i className="vtd-cross vtd-cross--sl" />SL{" "}
            <i className="vtd-dot vtd-dot--trail" />Trail
          </Row>
        </>
      ) : (
        <div className="vtd-row vtd-row--collapsed">
          <span className={`vtd-value ${d?.direction ? statusClass : "vtd-value--muted"}`}>{collapsedText}</span>
        </div>
      )}
    </div>
  );
};

export default ViruTrendDynamicsOverlay;